import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { TableSkeleton } from "common-fe";
import { useScribeIdentity } from "../hooks/useScribeIdentity";
import { filterVisibleClasses } from "score-common";
import { useTranslation } from "i18n-fe";
import AppShell from "./AppShell";
import PageBreadcrumbs from "./PageBreadcrumbs";
import SplitClassChip from "./SplitClassChip";
import ClassStandings from "./ClassStandings";
import { useScoreContext } from "../context/ScoreContext";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { useClassStatuses } from "../hooks/useClassStatuses";
import { useCatalogDefs } from "../hooks/useCatalogDefs";
import { useRingState } from "../hooks/useRingState";
import { scribePositionsFor } from "../util/scribeAssignment";
import {
  CLASS_SORT_CLASS_NUMBER,
  CLASS_SORT_TIME,
  getClassListSort,
  setClassListSort,
} from "../store/classListSort";
import {
  classDayKey,
  classEffectiveTime,
  classEffectiveTimeZone,
  classTestName,
  classTestVaries,
  formatVenueTime,
  nextScheduledClass,
  partitionClassesByDone,
  sortByClassNumber,
  venueTodayKey,
} from "../util/classDisplay";

// #1280 — keyed by DERIVED_CLASS_STATUSES (score-common). These used to be keyed
// by `Class.status`, a field classSchema never declared and nothing ever wrote,
// so every class fell through to the SCHEDULED default no matter its real state.
// Labels are i18n keys now, not literals.
const STATUS_COLORS = {
  SCHEDULED: { bg: "#e3f2fd", fg: "#1976d2", labelKey: "classes.status.scheduled" },
  IN_PROGRESS: { bg: "#fff3e0", fg: "#f57c00", labelKey: "classes.status.inProgress" },
  COMPLETE: { bg: "#e8f5e9", fg: "#388e3c", labelKey: "classes.status.complete" },
  PLACED: { bg: "#ede7f6", fg: "#5e35b1", labelKey: "classes.status.placed" },
  // #3645 - started but the ring is not on it. Grey and NOT a warning: the
  // scribe has nothing to do. "In Progress" stays live-ring only (#2109).
  PART_SCORED: { bg: "#eceff1", fg: "#455a64", labelKey: "classes.status.partScored" },
};

export default function ClassList() {
  const navigate = useNavigate();
  const { t } = useTranslation("score");
  const { sub } = useScribeIdentity(); // #3256: last known scribe when offline
  const { selectedShow } = useScoreContext();
  const { data: classes, loading, refetch, refreshing } = useClassesForShow(selectedShow?.id);
  // #1280 — real, ride-derived class state for the chip. Absent (still loading,
  // or offline with a cold cache) leaves the chip off entirely rather than
  // asserting "Scheduled" about a class we know nothing about.
  // #1557 — the same call now also yields each class's earliest ride time, for
  // the 84-of-189 classes that carry no scheduledTime of their own.
  // #2804 — this hook now skips its revalidate on a warm remount inside its
  // freshness window (see useClassStatuses), so its refetch is threaded into
  // the manual refresh button below: the ONE control that guarantees fresh
  // statuses on demand, same escape hatch #888 gave the class list itself.
  const {
    data: classStatusData,
    settled: classStatusesSettled,
    refetch: refetchStatuses,
    refreshing: statusesRefreshing,
  } = useClassStatuses(selectedShow?.id, classes);
  const classStatuses = classStatusData?.statuses;
  const rideProgress = classStatusData?.rideProgress;
  const earliestRideTimes = classStatusData?.earliestRideTimes;
  const earliestRideTimeZones = classStatusData?.earliestRideTimeZones;
  // card #1664 — table cache preferred over the bundled catalog for the
  // per-class test-name preview below (classTestName).
  const { catalogDefsById } = useCatalogDefs();

  // #1935/#2081 (docs/score/ring-state.md) — "current in ring" read-only
  // indicator (per class card, below). The Start class action itself moved
  // to the ride list page (#2081). Entirely optional and additive: nothing
  // here gates rendering or interaction with the rest of this screen (AC6).
  const ringState = useRingState(selectedShow?.id);

  // #910 — a scribe almost always only cares about TODAY, not every day of a
  // multi-day show. Default ON; toggling off reveals every assigned class
  // regardless of day. "Today" is the VENUE's wall-clock day
  // (America/Los_Angeles, #688) — never the scribe device's own zone — so
  // this compares classDayKey() against venueTodayKey(), both now the SAME
  // real timezone conversion (#1559; they used to be two different
  // derivations — a naive slice for the class, a real conversion for "now"
  // — which is how an evening class silently fell off this view). Computed
  // once per render, not per class, so a long list doesn't re-derive "today"
  // per row.
  const [todayOnly, setTodayOnly] = useState(true);
  // #2859 - which PLACED class cards have their final-standings panel open.
  const [openStandings, setOpenStandings] = useState(() => new Set());
  const toggleStandings = (classId) =>
    setOpenStandings((prev) => {
      const next = new Set(prev);
      if (next.has(classId)) next.delete(classId);
      else next.add(classId);
      return next;
    });
  const visibleClasses = useMemo(() => {
    // #1117 — a hidden class (hand-hidden, or an emptied class staff flagged as a split section, #3564)
    // drops out of the scribe's class list. Presentation only.
    const all = filterVisibleClasses(classes ?? []);
    if (!todayOnly) return all;
    // #1814 AC3 -- a marked class is compared against "today" in ITS zone.
    const now = new Date();
    return all.filter((c) => classDayKey(c) === venueTodayKey(now, c.scheduledTimeZone));
  }, [classes, todayOnly]);

  // #1557 — the next class with no scores in it, for the session-header chip.
  // Computed over visibleClasses, not the full list, so it honours the
  // today-only switch: an unscored class left behind on an EARLIER day would
  // otherwise stay "next" forever, which is the same species of permanently
  // wrong chip this card replaced. Order is untouched (visibleClasses is a
  // filter over useClassesForShow's scheduledTime sort), so "earliest" still
  // means earliest.
  const nextClass = useMemo(
    () => nextScheduledClass(visibleClasses, classStatuses),
    [visibleClasses, classStatuses],
  );
  const nextClassTime = nextClass
    ? classEffectiveTime(nextClass, earliestRideTimes)
    : null;
  const nextClassTimeZone = nextClass
    ? classEffectiveTimeZone(nextClass, earliestRideTimeZones)
    : undefined;

  // #135 / #1702 — split the (already time-ordered) visible list so a
  // scribe never has to scan past finished classes to reach the next one to
  // score: still-to-score classes render first, Completed/Placed classes
  // render below a divider, keeping their existing chronological order in
  // both halves. See partitionClassesByDone for the "done" definition.
  //
  // #3678 - a status change never moves a card. The partition is computed
  // ONCE and frozen; a later pass that only flips a class's status (it just
  // turned Complete/Placed) keeps every card where it was, the badge updates
  // and the card greys. It re-partitions only when the scribe taps refresh
  // (resortNonce), comes back to the screen (a fresh mount has no frozen
  // partition, so it uses whatever the cache knows then), flips the today-only
  // switch, a class is added or removed, or a class's scheduledTime changes.
  // On a cold first visit the first partition is made with statuses unknown,
  // so everything sits in the active group in time order and done classes
  // grey in place as their badges land.
  const frozenPartitionRef = useRef(null);
  const [resortNonce, setResortNonce] = useState(0);
  const { active: activeClasses, done: doneClasses } = useMemo(() => {
    const sig = visibleClasses.map((c) => `${c.id}|${c.scheduledTime ?? ""}`).join(",");
    const frozen = frozenPartitionRef.current;
    if (frozen && frozen.sig === sig && frozen.nonce === resortNonce) {
      const byId = new Map(visibleClasses.map((c) => [c.id, c]));
      return {
        active: frozen.activeIds.map((id) => byId.get(id)),
        done: frozen.doneIds.map((id) => byId.get(id)),
      };
    }
    const partition = partitionClassesByDone(visibleClasses, classStatuses);
    frozenPartitionRef.current = {
      sig,
      nonce: resortNonce,
      activeIds: partition.active.map((c) => c.id),
      doneIds: partition.done.map((c) => c.id),
    };
    return partition;
    // classStatuses is deliberately NOT a dependency: a status change must not
    // re-partition (#3678). It is read only when the partition is (re)made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleClasses, resortNonce]);

  // #2868 - user-facing sort control. Default "time" leaves the #632 order
  // untouched; "classNumber" re-sorts each half for DISPLAY ONLY. nextClass
  // above is deliberately derived from the time-ordered visibleClasses, so
  // the header's "next class" chip never changes with this control.
  const [sortBy, setSortBy] = useState(getClassListSort);
  const handleSortChange = (_e, value) => {
    // ToggleButtonGroup (exclusive) emits null on re-click of the active
    // button; ignore it so one option is always selected.
    if (value == null) return;
    setSortBy(value);
    setClassListSort(value);
  };
  const sortedActiveClasses = useMemo(
    () => (sortBy === CLASS_SORT_CLASS_NUMBER ? sortByClassNumber(activeClasses) : activeClasses),
    [sortBy, activeClasses],
  );
  const sortedDoneClasses = useMemo(
    () => (sortBy === CLASS_SORT_CLASS_NUMBER ? sortByClassNumber(doneClasses) : doneClasses),
    [sortBy, doneClasses],
  );

  // #3678 - the scribe never waits on the network. The list paints as soon as
  // the class list itself is known (cache or network); a class whose status is
  // not known yet shows a small spinner in its badge slot, not a whole-list
  // skeleton. (#2671/#2821 held the list behind TableSkeleton until the status
  // fetch landed; that blocked on the network and is gone.) `settled` still
  // resets on a real deps change, so the spinner also covers a today-toggle.
  const classStatusesLoading = (classes ?? []).length > 0 && !classStatusesSettled;
  const listLoading = loading;
  // #2804 — the manual refresh now drives THREE refetches (classes, statuses,
  // ring state); the button must read as busy until the last of them lands.
  const anyRefreshing = Boolean(refreshing || statusesRefreshing);

  // #3678 - the refresh button refetches, then re-partitions once the new
  // data is in (never before: re-sorting the old data would just undo itself).
  const resortAfterRefreshRef = useRef(false);
  useEffect(() => {
    if (!anyRefreshing && resortAfterRefreshRef.current) {
      resortAfterRefreshRef.current = false;
      setResortNonce((n) => n + 1);
    }
  }, [anyRefreshing]);

  const renderClassCard = (c) => {
    const rawStatus = classStatuses?.[c.id];
    // #1935 — "already current for its ring", independent of how it got
    // there (Start class, or simply the first ride of this class being
    // scored, #1934's SCORED path). #2109 (docs/score/ring-truth.md) — this
    // now goes through useRingState.isCurrentForRing, which itself calls
    // score-common's resolveRingTruth: the ONE source of "in progress" this
    // screen reads.
    const isCurrentInRing = ringState.isCurrentForRing(c.ring, c.id);
    // #2109 — the status chip's IN_PROGRESS label used to come straight off
    // deriveClassStatus (rawStatus), which is only "at least one ride is
    // past PENDING" (score-common/ride-queue.js). That is exactly #2083's
    // 9am-ghost bug: a class the scribe scored earlier and then moved away
    // from keeps its scoresheets open, so rawStatus stays IN_PROGRESS
    // forever even though the ring has long since moved on. The chip now
    // shows "In progress" only when the CENTRAL ring record says this class
    // is the ring's live occupant right now (suppressed once the class is
    // actually COMPLETE/PLACED, same as the ring-current chip below); a
    // rawStatus of IN_PROGRESS that the ring record doesn't confirm renders
    // no chip at all rather than repeating the stale claim (rule 8: no
    // consumer invents a status).
    const displayStatusKey =
      isCurrentInRing && rawStatus !== "COMPLETE" && rawStatus !== "PLACED"
        ? "IN_PROGRESS"
        : rawStatus === "IN_PROGRESS"
          ? "PART_SCORED" // #3645, was null (#2109 ghost) - never "In Progress"
          : rawStatus;
    const status = STATUS_COLORS[displayStatusKey] ?? null;
    // #3678 - a class that turns Complete/Placed after first paint stays in
    // place and greys; it moves below the divider only on the next re-sort.
    const isDone = rawStatus === "COMPLETE" || rawStatus === "PLACED";
    // #3678 - status not known yet: a small spinner in the badge slot.
    const statusPending = rawStatus === undefined && classStatusesLoading;
    const progress = rideProgress?.[c.id];
    // #627 — the seat this scribe is assigned in THIS class. Normally one;
    // a scribe named at two seats of the same class picks on entry.
    const positions = scribePositionsFor(c, sub);
    // #896 — full test name on the card, so the scribe can see/confirm
    // which test the class rides without opening it. Omitted (not a
    // placeholder) on a Test-of-Choice class with no single test hint.
    const testName = classTestName(c, catalogDefsById);
    // #1223 — Test-of-Choice (fvd-unmatched:9999 sentinel): say so, not nothing.
    const testLine = testName ?? (classTestVaries(c) ? t("classes.testVaries") : null);
    return (
      <Card
        key={c.id}
        variant="outlined"
        sx={{
          borderRadius: 3,
          transition: "box-shadow 0.2s, border-color 0.2s",
          opacity: isDone ? 0.55 : 1,
          "&:hover": {
            borderColor: "primary.main",
            boxShadow: 4,
          },
        }}
      >
        <CardActionArea
          data-testid="class-card"
          onClick={() => navigate(`/classes/${c.id}`)}
          sx={{ p: 0 }}
        >
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  fontWeight={700}
                >
                  {formatVenueTime(c.scheduledTime, { zone: c.scheduledTimeZone })}
                </Typography>
                {/* #1761 — a base class and its FVD ride-out/split section
                    carry identical classNumber/className text, so the split
                    card gets the same "Split/RO" chip #883 put on the
                    manual-add picker's rows. The chip sits OUTSIDE the h6:
                    the class label line's textContent is what specs (and
                    scribes scanning the card) read as the name. */}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6" fontWeight={600}>
                    {c.classNumber != null && `${c.classNumber}`.trim() !== ""
                      ? `${c.classNumber}. ${c.className}`
                      : c.className}
                  </Typography>
                  <SplitClassChip klass={c} testId="class-card-split-chip" />
                </Stack>
                {testLine && (
                  <Typography
                    variant="body2"
                    data-testid="class-test-name"
                    sx={{ color: "text.secondary" }}
                  >
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {t("rides.testPrefix")}
                    </Box>{" "}
                    {testLine}
                  </Typography>
                )}
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ mt: 1, color: "text.secondary" }}
                  divider={<span>•</span>}
                >
                  {c.ring && (
                    <Typography variant="body2">{c.ring}</Typography>
                  )}
                  {positions.length > 0 && (
                    <Typography variant="body2" fontWeight={600}>
                      Position {positions.join(" / ")}
                    </Typography>
                  )}
                </Stack>
              </Box>
              {statusPending && (
                <CircularProgress
                  size={20}
                  data-testid="class-status-pending"
                  aria-label={t("classes.status.loading")}
                />
              )}
              {status && (
                <Chip
                  data-testid="class-status"
                  label={
                    displayStatusKey === "PART_SCORED"
                      ? t(status.labelKey, {
                          scoredCount: progress?.scoredCount ?? 0,
                          total: progress?.total ?? 0,
                        })
                      : t(status.labelKey)
                  }
                  sx={{
                    bgcolor: status.bg,
                    color: status.fg,
                    fontWeight: 600,
                  }}
                />
              )}
            </Stack>
          </CardContent>
        </CardActionArea>

        {/* #1935/#2081 — "current in ring" indicator, OUTSIDE
            CardActionArea (RideList.jsx's discard-button pattern, so it
            never intercepts a tap into the class). The START CLASS action
            itself moved to the ride list page (#2081, beside Add Ride
            Manually) — this stays behind as a read-only status: a class
            already running in its ring shows the chip, otherwise nothing.
            Suppressed on a COMPLETE/PLACED class (docs/score/ring-state.md)
            same as before, since a finished class can't be "current". */}
        {/* #2859 - a PLACED class expands in place into its final standings
            (placing, each judge's percentage, composite). The toggle sits
            OUTSIDE CardActionArea like the ring chip below, so the card body
            still opens the ride list: a scribe does go back into a placed
            class (#1702). The panel (and its fetch) mounts only while open. */}
        {rawStatus === "PLACED" && (
          <>
            <Box sx={{ px: 2, pb: 1.5 }}>
              <Button
                size="small"
                data-testid={`class-standings-toggle-${c.id}`}
                aria-expanded={openStandings.has(c.id)}
                aria-controls={`class-standings-${c.id}`}
                onClick={() => toggleStandings(c.id)}
                startIcon={openStandings.has(c.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ color: "text.primary" }}
              >
                {openStandings.has(c.id) ? t("classes.standings.hide") : t("classes.standings.show")}
              </Button>
            </Box>
            <Collapse in={openStandings.has(c.id)} unmountOnExit>
              <Box id={`class-standings-${c.id}`}>
                <ClassStandings classId={c.id} placedByDivision={c.placedByDivision} />
              </Box>
            </Collapse>
          </>
        )}

        {c.ring && isCurrentInRing && rawStatus !== "COMPLETE" && rawStatus !== "PLACED" && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Chip
              size="small"
              color="success"
              variant="outlined"
              data-testid={`class-ring-current-${c.id}`}
              label={t("classes.ringState.current", { ring: c.ring })}
            />
          </Box>
        )}
      </Card>
    );
  };

  return (
    <AppShell
      showNextClass
      nextClass={nextClass}
      nextClassTime={nextClassTime}
      nextClassTimeZone={nextClassTimeZone}
      // #2196 — nextClass is derived from `classes`/`classStatuses`, so the
      // header's "No upcoming class" must stay a loading chip until BOTH of
      // those fetches have actually settled, not flash a confident negative
      // off an empty in-flight list. `classStatusData === undefined` (not a
      // separate `classStatusLoading` flag) is the real signal here: useClassStatuses
      // is keyed on the class list, which starts empty, so its own `loading`
      // flag settles false the instant that empty pass resolves and never
      // resets when the list fills in and the real (slow) status fetch
      // starts (#2162) — see useClassStatuses.js's fetchRemote guard, which
      // now returns `undefined` for exactly this reason.
      // The `(classes ?? []).length > 0` half is what stops that sentinel from
      // becoming a PERMANENT spinner on a show with zero classes: with an empty
      // list BOTH of useClassStatuses' branches return `undefined` by design, so
      // nothing ever settles it and the chip would spin forever, which is the
      // exact defect this card exists to remove. The sentinel is only meaningful
      // once there is at least one class whose status could still be in flight;
      // `loading` already covers the window before the class list itself has
      // settled, so a list that has settled EMPTY correctly reads as a known
      // "No upcoming class". Gating here rather than making the hook's sentinel
      // list-aware keeps the whole rule in one file, next to the `classes` and
      // `classStatuses` that nextClass is actually derived from, instead of
      // resting on useClassesForShow's null-vs-empty-array convention.
      // #2671 — this is now the same `listLoading` the class list itself
      // gates on below, so the header chip and the list settle together.
      nextClassLoading={listLoading || classStatusesLoading}
    >
      <PageBreadcrumbs items={[{ label: t("classes.title") }]} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {t("classes.title")}
        </Typography>
        <FormControlLabel
          sx={{ color: "text.primary", mr: 0 }}
          control={
            <Switch
              data-testid="today-only-switch"
              checked={todayOnly}
              onChange={(e) => setTodayOnly(e.target.checked)}
            />
          }
          label={t("classes.todayOnly")}
        />
      </Stack>

      {/* #2868 - sort control. Time is the default (#632); Class # is the
          alternative. Exclusive toggle group: two touch-sized buttons, no
          menu to open, fits the kiosk. */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }} id="class-sort-label">
          {t("classes.sort.label")}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={sortBy}
          onChange={handleSortChange}
          aria-labelledby="class-sort-label"
          data-testid="class-sort-control"
        >
          <ToggleButton value={CLASS_SORT_TIME} data-testid="class-sort-time">
            {t("classes.sort.time")}
          </ToggleButton>
          <ToggleButton value={CLASS_SORT_CLASS_NUMBER} data-testid="class-sort-class-number">
            {t("classes.sort.classNumber")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* #888 — mid-show scribe reassignment sits invisible behind the
          IndexedDB cache (useClassesForShow only revalidates on mount/dep
          change) until a full app reload re-mounts the hook. This is the
          one kiosk-appropriate escape hatch: a manual refresh that forces
          useCachedResource's refetch() past the cache. Sticky (top:105 —
          same AppBar(64)+SessionHeader offset ScoringForm's sticky bar
          uses, #647) and pinned top-left of the list so it stays reachable
          while scrolling a long class list, without competing with the
          page title. Own bg/border/shadow (not color="primary" — brand
          primary.main is a near-black grey that all but disappears against
          a dark-mode background, #862) so it reads clearly in both themes. */}
      <Box
        sx={{
          position: "sticky",
          top: 105,
          zIndex: 1100,
          display: "flex",
          justifyContent: "flex-start",
          mb: 1,
        }}
      >
        <Tooltip title={anyRefreshing ? t("classes.refreshing") : t("classes.refresh")}>
          <span>
            <IconButton
              data-testid="refresh-classes-button"
              aria-label={t("classes.refresh")}
              onClick={() => {
                resortAfterRefreshRef.current = true;
                refetch();
                // #2804 — statuses ride a freshness window now (see
                // useClassStatuses), so a warm remount no longer re-pulls
                // them; this button is the control that forces them fresh
                // anyway. Before this card the button refreshed ONLY the
                // class list, leaving the chips (a separate hook) stale.
                refetchStatuses?.();
                // #1935 — the manual refresh also picks up a ring's state
                // changing from elsewhere (another scribe's Start class,
                // Score Admin, or a scoring write), same escape hatch as
                // the class list's own cache.
                ringState.refetch();
              }}
              disabled={anyRefreshing}
              sx={{
                bgcolor: "background.paper",
                color: "text.primary",
                border: 1,
                borderColor: "divider",
                boxShadow: 2,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <RefreshIcon
                fontSize="small"
                sx={
                  anyRefreshing
                    ? {
                        animation: "classListRefreshSpin 1s linear infinite",
                        "@keyframes classListRefreshSpin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                      }
                    : undefined
                }
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* #714 — card list not yet loaded: shaped skeleton rows, not a bare
          centered spinner, so the screen reads as "content coming" rather
          than "something is broken/blank". #2671 — this now also covers the
          "classes are known but statuses aren't yet" window (listLoading),
          so the card list itself never paints ahead of the completed/active
          split being knowable. On a warm remount classStatuses is warm-
          cached too (see useClassStatuses), so listLoading is already false
          on the very first render and this never shows at all. */}
      {listLoading && (
        <Box sx={{ mt: 2 }}>
          <TableSkeleton rows={5} dense={false} />
        </Box>
      )}

      {!listLoading && (classes ?? []).length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 3 }}>
          {t("classes.empty")}
        </Typography>
      )}

      {/* #910 — the scribe HAS assigned classes, just none scheduled today
          (e.g. viewing the class list the evening before a show, or after the
          venue's last class of the day). Distinct from classes.empty above
          (no assignments at all) so the hint to turn the switch off only
          shows when it would actually reveal something. */}
      {!listLoading && (classes ?? []).length > 0 && visibleClasses.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 3 }}>
          {t("classes.emptyToday")}
        </Typography>
      )}

      {/* #2671 — gated on listLoading (not just `loading`) so a completed
          class can never paint into this ACTIVE group for even one frame
          while classStatuses is still unknown: the whole list stays behind
          the skeleton above until the partition is knowable, instead of
          rendering everything as "active" and correcting it a moment later. */}
      {!listLoading && (
        <Stack spacing={2} sx={{ mt: 2 }} data-testid="active-classes">
          {sortedActiveClasses.map(renderClassCard)}
        </Stack>
      )}

      {/* #135 / #1702 — Completed/Placed classes stay visible and openable
          (a scribe does go back into a placed class to check something),
          just pushed below a labeled divider so they never sit ahead of the
          next class still to score. Only rendered once there IS a done
          class — an all-active list shows no divider at all. */}
      {!listLoading && doneClasses.length > 0 && (
        <>
          <Divider
            data-testid="completed-divider"
            sx={{ mt: activeClasses.length > 0 ? 4 : 2, mb: 2 }}
          >
            <Typography variant="overline" color="text.secondary">
              {t("classes.completedDivider")}
            </Typography>
          </Divider>
          <Stack spacing={2} data-testid="done-classes">
            {sortedDoneClasses.map(renderClassCard)}
          </Stack>
        </>
      )}
    </AppShell>
  );
}
